import { format } from "date-fns";
import { AuditEditType, TransformedDuration, TransformedPairOrDuration } from "../types";
import { INTERNAL_USER_ROLES } from "../../../../../ManageUsers/constants";

/**
 * Formats a date to the standard audit trail format: "M/d/yyyy h:mmaaa" in lowercase
 * @param date - The date to format
 * @returns Formatted date string or empty string if date is undefined
 */
export const formatEditDate = (date?: Date): string => {
  if (!date) return "";
  return format(date, "M/d/yyyy h:mmaaa").toLowerCase();
};

/**
 * Checks if an edit is a synthetic original (expected shift times)
 * @param edit - The edit to check
 * @returns True if the edit is a synthetic original
 */
export const isSyntheticOriginal = (edit: TransformedPairOrDuration): boolean => {
  return edit.editType === AuditEditType.ORIGINAL && !edit.updateDate && !edit.updateUser;
};

/**
 * Gets the display text for an edit type
 * @param editType - The edit type to get display text for
 * @returns The display text for the edit type
 */
export const getEditTypeDisplayText = (editType: AuditEditType): string => {
  switch (editType) {
    case AuditEditType.ORIGINAL:
      return "Original timestamp";
    case AuditEditType.CREW_MEMBER:
      return "Crew Member edit";
    case AuditEditType.CLIENT:
      return "Client edit";
    case AuditEditType.BLUECREW_SUPPORT:
      return "Bluecrew Support edit";
    case AuditEditType.CLIENT_SUPPORT:
      return "Client/Support edit";
    default:
      // If there is an error, it means there is a case missing
      return `${editType satisfies never}`;
  }
};

/**
 * Gets the display label for an edit
 * @param edit - The edit to get the label for
 * @returns The edit label text
 */
export const getEditLabel = (edit: TransformedPairOrDuration): string => {
  let description = getEditTypeDisplayText(edit.editType);
  if (edit.deleted) {
    description += " (Deleted)";
  }
  return description;
};

/**
 * Gets the formatted name of the user who made the edit, with role information
 * Only shows author for edit types that should display author information
 * @param edit - The edit to get the user name from
 * @returns Formatted user name with role, or empty string if no user or edit type shouldn't show author
 */
export const getEditAuthor = (edit: TransformedPairOrDuration): string => {
  if (edit.editType === AuditEditType.ORIGINAL || edit.editType === AuditEditType.CREW_MEMBER) {
    return "";
  }

  if (!edit.updateUser) return "";

  const userName = edit.updateUser.name || "Unknown";
  if (edit.editType === AuditEditType.CLIENT_SUPPORT && INTERNAL_USER_ROLES.includes(edit.updateUser.userType)) {
    return `${userName} (Bluecrew) `;
  }
  return `${userName} `;
};

/**
 * Gets the formatted edit date string for display (without author)
 * @param edit - The edit to get the date for
 * @returns Formatted date string, or "Expected shift times" for synthetic originals,
 *          or "Current data" for CLIENT_SUPPORT without date, or empty string if no date available
 */
export const getEditDate = (edit: TransformedPairOrDuration): string => {
  if (isSyntheticOriginal(edit)) {
    return "Expected shift times";
  }

  if (edit.editType === AuditEditType.CLIENT_SUPPORT && !edit.updateDate) {
    return "Latest/Most recent edit";
  }

  return formatEditDate(edit.updateDate);
};

export const isTransformedDuration = (
  item: TransformedPairOrDuration
) : item is TransformedDuration => 'breakDurationMinutes' in item;
